import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React from 'react';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { handleUnexpectedError } from '@/app/errors';
import { useCurrentUser } from '@/app/hooks/use-current-user';
import { useAppForm } from '@/app/lib/tanstack-form';
import { routeMetadata } from '@/app/lib/tanstack-router';
import { confirmSmsVerificationCode, requestSmsVerificationCode } from '@/app/network/firefly-api';
import { Button, ButtonGroup } from '@/common/components/button';
import { Description, Field, FieldGroup, FieldInfo, FieldRow, Fieldset, Label } from '@/common/components/fieldset';
import { FormInput } from '@/common/components/form-input';
import { Heading } from '@/common/components/heading';
import { Text } from '@/common/components/text';
import { isNullOrWhitespace } from '@/common/string';
import { SettingsSidebar } from '@/settings/components/settings-sidebar';
import { ContentSection } from '@/shared/components/content-section';
import { Toast } from '@/shared/components/toast';
import { phoneNumberMask, smsVerificationCodeMask } from '@/shared/masks';
import { cleanPhoneNumber } from '@/shared/phone-numbers';
import { getCurrentUserQueryOptions } from '@/shared/user/queries';

export const Route = createFileRoute('/settings/_layout/user-profile/update-sms')({
  head: () => ({
    meta: routeMetadata('Update SMS Phone Number')
  }),
  component: UpdateSmsPage
});

function UpdateSmsPage() {
  const { id: userId, sms_phone_number } = useCurrentUser();
  const [codeSent, setCodeSent] = React.useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const requestSmsVerificationCodeMutation = useMutation({
    mutationFn: async (value: { phone: string }) => requestSmsVerificationCode(userId, cleanPhoneNumber(value.phone)),
    onSuccess: () => setCodeSent(true),
    onError: error => handleUnexpectedError(error)
  });

  const confirmSmsVerificationCodeMutation = useMutation({
    mutationFn: async (value: { phone: string; code: string }) => confirmSmsVerificationCode(userId, cleanPhoneNumber(value.phone), parseInt(value.code)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getCurrentUserQueryOptions()['queryKey'] });
      toast.success(Toast, { data: { title: 'SMS phone number updated successfully.' } });
      await navigate({ to: '/settings/user-profile' });
    },
    onError: error => handleUnexpectedError(error)
  });

  const form = useAppForm({
    defaultValues: {
      phone: '',
      code: ''
    },
    validators: {
      onBlur: z.object({
        phone: z.string()
          .min(1, 'Mobile Phone is required.')
          .superRefine((value, ctx) => {
            if (isNullOrWhitespace(value)) return;

            if (cleanPhoneNumber(value) === sms_phone_number?.replace('+1', '')) {
              ctx.addIssue({
                code: 'custom',
                message: 'Phone number is already set.'
              });
              return;
            }
          }),
        code: z.string().min(1, 'Verification Code is required.')
      })
    },
    onSubmit: async ({ value }) => {
      await confirmSmsVerificationCodeMutation.mutateAsync({ ...value });
    }
  });

  const handleSendCodeClick = () => {
    form.validateField('phone', 'blur');
    form.fieldInfo.phone.instance?.handleBlur();

    if (form.getFieldMeta('phone')?.isValid) {
      requestSmsVerificationCodeMutation.mutate({ phone: form.state.values.phone });
    }
  };

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <SettingsSidebar active="user-profile"/>
      <ContentSection bg="white">
        <Heading>Update SMS Phone Number</Heading>

        <form onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit(event);
        }}
        >
          <Fieldset>
            <Text>Verify a mobile number so you can receive text message notifications.</Text>
            <FieldGroup>
              <FieldRow cols={2}>
                <form.AppField name="phone">
                  {field => (
                    <Field>
                      <Label>Mobile Phone</Label>
                      <div data-slot="control" className="">
                        <FormInput
                          type="tel"
                          placeholder="(850) 555-6666"
                          mask={phoneNumberMask}
                          className="flex-1"
                          autoComplete="tel"
                          disabled={codeSent}
                        />
                      </div>
                      <FieldInfo field={field} />
                      {!codeSent && (
                        <Description>We will text a verification code to this number. Standard text message fees may apply.</Description>
                      )}
                    </Field>
                  )}
                </form.AppField>
              </FieldRow>

              {codeSent && (
                <FieldRow cols={2}>
                  <form.AppField name="code">
                    {field => (
                      <Field>
                        <Label>Verification Code</Label>
                        <FormInput
                          type="text"
                          inputMode="numeric"
                          mask={smsVerificationCodeMask}
                          placeholder="Enter 6-digit code"
                          autoComplete="one-time-code"
                        />
                        <FieldInfo field={field} />
                        <Description>Enter the verification code sent to your phone.</Description>
                      </Field>
                    )}
                  </form.AppField>
                </FieldRow>
              )}
            </FieldGroup>
          </Fieldset>

          <ButtonGroup>
            <Button plain to="/settings/user-profile">Cancel</Button>
            {!codeSent && (
              <Button onClick={handleSendCodeClick} disabled={requestSmsVerificationCodeMutation.isPending}>
                {requestSmsVerificationCodeMutation.isPending ? 'Sending...' : 'Send Code'}
              </Button>
            )}
            {codeSent && (
              <form.Subscribe selector={state => state.isSubmitting}>
                {isSubmitting => (
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Verifying Number...' : 'Verify Number'}
                  </Button>
                )}
              </form.Subscribe>
            )}
          </ButtonGroup>
        </form>

      </ContentSection>
    </main>
  );
}
